/**
 * Business Plan Quality Scorecard Component
 * Displays Enloop-style quality scoring and validation
 * Completely optional - doesn't modify the plan data
 */

import React, { useState, useEffect } from 'react';
import { BusinessPlan, SectionId } from '../../types/businessPlan';
import { 
  BusinessPlanScore, 
  ScoreCalculationResult 
} from '../../types/businessPlanScoring';
import { 
  calculateBusinessPlanScore, 
  getPlanScore, 
  savePlanScore 
} from '../../services/planScoringService';

interface PlanQualityScorecardProps {
  plan: BusinessPlan;
  onScoreCalculated?: (score: BusinessPlanScore) => void;
}

const PlanQualityScorecard: React.FC<PlanQualityScorecardProps> = ({ 
  plan, 
  onScoreCalculated 
}) => {
  const [scoreData, setScoreData] = useState<ScoreCalculationResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate score when component mounts or plan changes
  useEffect(() => {
    calculateScore();
  }, [plan.id, plan.updated_at]);

  const calculateScore = async () => {
    if (!plan.id) return;
    
    setIsCalculating(true);
    try {
      // Try to get saved score first
      const savedScore = await getPlanScore(plan.id);
      
      if (savedScore) {
        // Use saved score but recalculate summary
        const freshCalculation = calculateBusinessPlanScore(plan);
        setScoreData({
          score: savedScore,
          summary: freshCalculation.summary
        });
      } else {
        // Calculate fresh score
        const result = calculateBusinessPlanScore(plan);
        setScoreData(result);
        
        // Optionally save to database (non-blocking)
        if (plan.user_id) {
          savePlanScore(result.score).catch(err => 
            console.log('Could not save score:', err)
          );
        }
      }
      
      if (onScoreCalculated && scoreData) {
        onScoreCalculated(scoreData.score);
      }
    } catch (error) {
      console.error('Error calculating score:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  if (!scoreData) {
    return null;
  }

  const { score, summary } = scoreData;
  const scoreColor = score.overall_score >= 80 ? 'green' : 
                     score.overall_score >= 60 ? 'yellow' : 'red';

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Plan Quality Score</h3>
            <p className="text-sm text-gray-500">AI-powered quality analysis</p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          {isExpanded ? 'Show Less' : 'Show Details'}
        </button>
      </div>

      {/* Score Display */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Overall Score */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
          <div className="text-sm text-gray-600 mb-1">Overall Score</div>
          <div className="flex items-baseline space-x-2">
            <span className={`text-4xl font-bold ${
              scoreColor === 'green' ? 'text-green-600' :
              scoreColor === 'yellow' ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {score.overall_score}
            </span>
            <span className="text-xl text-gray-500">/100</span>
            <span className={`ml-2 px-2 py-1 rounded text-sm font-semibold ${
              scoreColor === 'green' ? 'bg-green-100 text-green-700' :
              scoreColor === 'yellow' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
            }`}>
              Grade {summary.grade}
            </span>
          </div>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${
                  scoreColor === 'green' ? 'bg-green-500' :
                  scoreColor === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${score.overall_score}%` }}
              />
            </div>
          </div>
        </div>

        {/* Completeness */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
          <div className="text-sm text-gray-600 mb-1">Completeness</div>
          <div className="flex items-baseline space-x-2">
            <span className="text-3xl font-bold text-purple-600">
              {score.sections_completed}
            </span>
            <span className="text-lg text-gray-500">/ {score.sections_total}</span>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            {score.completeness_score}% Complete
          </div>
          <div className="mt-1">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full bg-purple-500 transition-all duration-500"
                style={{ width: `${score.completeness_score}%` }}
              />
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100 flex flex-col justify-center">
          <div className="text-sm text-gray-600 mb-2">Validation Status</div>
          <div className={`inline-flex items-center px-3 py-2 rounded-lg font-semibold text-sm ${
            score.validation_status === 'pass' ? 'bg-green-100 text-green-700' :
            score.validation_status === 'needs-improvement' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {score.validation_status === 'pass' && '✓ Ready for Review'}
            {score.validation_status === 'needs-improvement' && '⚠ Needs Work'}
            {score.validation_status === 'fail' && '✗ Not Ready'}
          </div>
        </div>
      </div>

      {/* Quick Summary */}
      {!isExpanded && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-700">
            <span className="font-semibold">Quick Tip:</span>{' '}
            {summary.nextSteps[0] || 'Your plan looks good! Keep refining it.'}
          </div>
        </div>
      )}

      {/* Detailed View */}
      {isExpanded && (
        <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
          {/* Next Steps */}
          {summary.nextSteps.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Next Steps
              </h4>
              <ul className="space-y-1">
                {summary.nextSteps.map((step, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex items-start">
                    <span className="mr-2">•</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Strengths */}
          {summary.strengths.length > 0 && (
            <div className="bg-green-50 rounded-lg p-4 border border-green-100">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Strengths
              </h4>
              <ul className="space-y-1">
                {summary.strengths.map((strength, idx) => (
                  <li key={idx} className="text-sm text-gray-700">{strength}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Areas for Improvement */}
          {summary.improvements.length > 0 && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Areas for Improvement
              </h4>
              <ul className="space-y-1">
                {summary.improvements.map((improvement, idx) => (
                  <li key={idx} className="text-sm text-gray-700">{improvement}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Detailed Recommendations */}
          {score.recommendations.length > 0 && (
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
              <h4 className="font-semibold text-gray-900 mb-2">Detailed Recommendations</h4>
              <ul className="space-y-1">
                {score.recommendations.slice(0, 5).map((rec, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex items-start">
                    <span className="mr-2 text-purple-600">→</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Refresh Button */}
          <div className="text-center pt-2">
            <button
              onClick={calculateScore}
              disabled={isCalculating}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              {isCalculating ? 'Recalculating...' : 'Recalculate Score'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanQualityScorecard;
